import { createRng } from "../rng";
import {
	type BaseState,
	type GameDefinition,
	type MoveOutcome,
	otherPlayer,
	type PlayerIndex,
	reject,
} from "../types";

/**
 * Snakes and Ladders, with one change: you are given two dice and choose which
 * to use.
 *
 * The original is pure luck — there is no decision anywhere in it — which makes
 * a computer opponent meaningless and a difficulty setting a lie. Offering two
 * numbers restores the only interesting question the board actually contains:
 * which of these two squares would I rather be standing on? Everything else is
 * the game as everybody already knows it.
 */

export const LADDER_SQUARES = 60;
export const LADDER_COLUMNS = 10;
export const LADDER_ROWS = LADDER_SQUARES / LADDER_COLUMNS;

/**
 * Where a square sits on the board, as a fraction of its width and height.
 *
 * A real snakes and ladders board starts at the bottom left and snakes back and
 * forth upwards, so square 1 is under square 20 and you climb towards the
 * finish. Numbering it like a spreadsheet — left to right from the top — is the
 * one thing that makes the board unreadable, because every ladder then appears
 * to go downwards.
 */
export function squarePosition(square: number): { x: number; y: number } {
	const index = square - 1;
	const row = Math.floor(index / LADDER_COLUMNS);
	const along = index % LADDER_COLUMNS;
	// Alternate rows run right to left, which is what makes it a snake.
	const column = row % 2 === 0 ? along : LADDER_COLUMNS - 1 - along;
	return {
		x: (column + 0.5) / LADDER_COLUMNS,
		// Row 0 is the bottom row.
		y: (LADDER_ROWS - 0.5 - row) / LADDER_ROWS,
	};
}
/** Turns before the race is called on whoever is further up. */
export const SNAKE_MOVE_LIMIT = 90;

/** Bottom → top. Climbing one of these is the whole joy of the game. */
export const LADDERS: Readonly<Record<number, number>> = {
	3: 17,
	8: 26,
	15: 34,
	21: 42,
	28: 51,
	40: 57,
};

/** Head → tail. */
export const SNAKES: Readonly<Record<number, number>> = {
	19: 5,
	31: 12,
	38: 20,
	47: 24,
	53: 33,
	58: 41,
};

/** Where a square sends you, or the square itself when it is an ordinary one. */
export function slideFrom(square: number): number {
	return LADDERS[square] ?? SNAKES[square] ?? square;
}

export interface SnakesLaddersState extends BaseState {
	kind: "snakesladders";
	at: [number, number];
	/** The two numbers on offer this turn, or null before they are rolled. */
	offer: [number, number] | null;
	/**
	 * Fixes every roll of the game at the start, so `apply` stays a pure
	 * function of the state. Redacted: holding it would mean knowing every
	 * throw before it happened.
	 */
	rollSeed: number;
	rollsUsed: number;
	movesMade: number;
	lastNote: string;
}

export type SnakesLaddersMove = { roll: true } | { use: 0 | 1 };

function diceAt(seed: number, index: number): [number, number] {
	const rng = createRng((seed + index * 104_729) >>> 0);
	return [1 + rng.int(6), 1 + rng.int(6)];
}

export const snakesLadders: GameDefinition<SnakesLaddersState, SnakesLaddersMove> = {
	id: "snakesladders",
	category: "classics",
	title: "Snakes and Ladders",
	emoji: "🐍",
	blurb: "Climb the ladders, dodge the snakes. Two dice — you pick which to use.",
	howTo: [
		"Roll, and you get two numbers. Choose the one you want to move.",
		"Land at the bottom of a ladder and you climb it.",
		"Land on a snake's head and you slide all the way down.",
	],

	create(rng) {
		return {
			kind: "snakesladders",
			at: [0, 0],
			offer: null,
			rollSeed: rng.int(0x7fffffff),
			rollsUsed: 0,
			movesMade: 0,
			lastNote: "Roll to start!",
			turn: 0,
			scores: [0, 0],
			result: null,
		};
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const value = raw as { roll?: unknown; use?: unknown };
		if (value.roll === true) return { roll: true };
		if (value.use === 0 || value.use === 1) return { use: value.use };
		return null;
	},

	apply(state, player, move): MoveOutcome<SnakesLaddersState> {
		if (state.result) return reject("This race is already finished.");
		if (state.turn !== player) return reject("It's not your turn yet.");

		if ("roll" in move) {
			if (state.offer !== null) return reject("Choose a number first.");
			const offer = diceAt(state.rollSeed, state.rollsUsed);
			return {
				ok: true,
				state: {
					...state,
					offer,
					rollsUsed: state.rollsUsed + 1,
					lastNote: `Rolled ${offer[0]} and ${offer[1]} — take your pick.`,
				},
			};
		}

		if (state.offer === null) return reject("Roll the dice first.");

		const step = state.offer[move.use];
		const at: [number, number] = [...state.at];
		// Overshooting the last square bounces you back, so the finish is exact.
		const raw = at[player] + step;
		const landed = raw > LADDER_SQUARES ? LADDER_SQUARES - (raw - LADDER_SQUARES) : raw;
		const after = slideFrom(landed);

		at[player] = after;
		const movesMade = state.movesMade + 1;
		const note =
			after > landed
				? "Up the ladder! 🪜"
				: after < landed
					? "Down the snake! 🐍"
					: `Moved ${step}.`;

		const next: SnakesLaddersState = {
			...state,
			at,
			offer: null,
			movesMade,
			// A copy, not the array itself — sharing it would let a mutation of
			// one silently rewrite the other.
			scores: [...at],
			lastNote: note,
		};

		if (after >= LADDER_SQUARES) {
			return { ok: true, state: { ...next, turn: null, result: { winner: player } } };
		}
		if (movesMade >= SNAKE_MOVE_LIMIT) {
			const winner = at[0] === at[1] ? null : at[0] > at[1] ? 0 : 1;
			return { ok: true, state: { ...next, turn: null, result: { winner } } };
		}
		return { ok: true, state: { ...next, turn: otherPlayer(player) } };
	},

	legalMoves(state, player) {
		if (state.result || state.turn !== player) return [];
		if (state.offer === null) return [{ roll: true }];
		// Both numbers are always playable — bouncing back handles an overshoot.
		return [{ use: 0 }, { use: 1 }];
	},

	/** The seed is every future roll at once, so it never leaves the server. */
	redact(state) {
		return { ...state, rollSeed: 0 };
	},

	tick() {
		return null;
	},

	nextTickAt() {
		return null;
	},
};

/** Where a player would end up using the nth number on offer. */
export function landingFor(state: SnakesLaddersState, player: PlayerIndex, use: 0 | 1): number {
	if (state.offer === null) return state.at[player];
	const raw = state.at[player] + state.offer[use];
	const landed = raw > LADDER_SQUARES ? LADDER_SQUARES - (raw - LADDER_SQUARES) : raw;
	return slideFrom(landed);
}

/**
 * Take whichever number leaves you further up. That is the entire skill of the
 * game and it is not a deep one — but it is a real choice, and a bot that
 * ignores snakes is genuinely worse than one that doesn't.
 */
export function bestLadderChoice(
	state: SnakesLaddersState,
	player: PlayerIndex,
): SnakesLaddersMove | null {
	if (state.offer === null) return { roll: true };
	return landingFor(state, player, 0) >= landingFor(state, player, 1) ? { use: 0 } : { use: 1 };
}
